import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiHome, FiArrowLeft, FiSearch } from 'react-icons/fi';
import { Helmet } from 'react-helmet-async';

export const NotFoundPage = () => {
  return (
    <>
      <Helmet>
        <title>Page Not Found | Nitro Logs</title>
      </Helmet>

      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute inset-0 luxury-dot-pattern opacity-30" />
        <div className="absolute top-0 left-0 w-full h-96 bg-linear-to-b from-primary-50 to-transparent" />
        <div className="absolute bottom-0 right-0 w-full h-96 bg-linear-to-t from-secondary-50 to-transparent" />
        
        {/* Floating Shapes */}
        <motion.div
          animate={{ 
            y: [0, -20, 0],
            rotate: [0, 5, 0]
          }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-20 left-20 w-64 h-64 bg-primary-200 rounded-full blur-3xl opacity-20"
        />
        <motion.div
          animate={{ 
            y: [0, 20, 0],
            rotate: [0, -5, 0]
          }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute bottom-20 right-20 w-96 h-96 bg-secondary-200 rounded-full blur-3xl opacity-20"
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl w-full text-center relative z-10"
        >
          {/* 404 Number */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <h1 className="text-[12rem] font-display font-bold leading-none">
              <span className="gradient-text">4</span>
              <span className="text-luxury-900">0</span>
              <span className="gradient-text">4</span>
            </h1>
            
            {/* Decorative Elements */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 border-2 border-dashed border-primary-200 rounded-full"
            />
          </motion.div>

          {/* Title */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-3xl font-display font-bold text-luxury-900 mt-8 mb-4"
          >
            Page Not Found
          </motion.h2>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-lg text-luxury-600 mb-8 max-w-md mx-auto"
          >
            The page you're looking for doesn't exist or has been moved. 
            Let's get you back on track.
          </motion.p>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="max-w-sm mx-auto mb-8"
          >
            <div className="relative">
              <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-luxury-400" />
              <input
                type="text"
                placeholder="Search for accounts..."
                className="input-luxury pl-12 pr-4"
              />
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="flex flex-wrap gap-4 justify-center"
          >
            <Link to="/">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="luxury-button group"
              >
                <FiHome className="inline mr-2" />
                Go to Homepage
              </motion.button>
            </Link>
            <Link to="/accounts">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="luxury-button-outline group"
              >
                <FiArrowLeft className="inline mr-2" />
                Browse Accounts
              </motion.button>
            </Link>
          </motion.div>

          {/* Help Links */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-12 text-sm text-luxury-500"
          >
            <span>Need help? </span>
            <Link to="https://t.me/Nitrologs9" target="_blank" className="text-primary-600 hover:text-primary-700 transition-colors">
              Contact Support
            </Link>
            <span className="mx-2">•</span>
            <Link to="https://t.me/Nitrologs9" target="_blank" className="text-primary-600 hover:text-primary-700 transition-colors">
              Visit Help Center
            </Link>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-8 pt-8 border-t border-luxury-200"
          >
            <p className="text-sm text-luxury-500 mb-4">Popular Categories:</p>
            <div className="flex flex-wrap gap-3 justify-center">
              {['Facebook', 'Instagram', 'Twitter', 'WhatsApp', 'TikTok'].map((category) => (
                <Link
                  key={category}
                  to={`/accounts?type=${category}`}
                  className="px-4 py-2 bg-luxury-100 text-luxury-600 rounded-xl text-sm hover:bg-primary-100 hover:text-primary-600 transition-colors"
                >
                  {category}
                </Link>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </>
  );
};